import { Dialog, Transition } from "@headlessui/react";
import { CheckIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";

export type OcrBulkDecision = "accept" | "reject";
/** "gate" is the Open For GRN interception: the same list, but both decisions are offered. */
export type OcrBulkDecisionVariant = OcrBulkDecision | "gate";

// Shared box model so all three footer buttons line up on one row, whichever of them is rendered.
const FOOTER_BUTTON_CLASSNAME =
  "inline-flex flex-1 sm:flex-none justify-center items-center shadow-sm px-5 border rounded-lg h-9 font-semibold text-xs whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const ACCEPT_BUTTON_CLASSNAME = `${FOOTER_BUTTON_CLASSNAME} bg-black hover:bg-gray-800 border-black text-white`;
const REJECT_BUTTON_CLASSNAME = `${FOOTER_BUTTON_CLASSNAME} bg-white hover:bg-red-50 border-red-300 text-red-600`;
const GO_BACK_BUTTON_CLASSNAME = `${FOOTER_BUTTON_CLASSNAME} bg-white hover:bg-gray-50 border-gray-300 text-gray-700`;

const VARIANT_COPY: Record<
  OcrBulkDecisionVariant,
  { title: string; subtitle: string; iconClassName: string }
> = {
  accept: {
    title: "Accept all changes",
    subtitle: "This applies to the whole purchase order and cannot be undone.",
    iconClassName: "bg-gray-100 text-gray-900",
  },
  reject: {
    title: "Reject all changes",
    subtitle: "This applies to the whole purchase order and cannot be undone.",
    iconClassName: "bg-red-100 text-red-600",
  },
  gate: {
    title: "Invoice changes are not resolved",
    subtitle: "Decide on the invoice differences before this PO can be opened for GRN.",
    iconClassName: "bg-amber-100 text-amber-600",
  },
};

/**
 * Confirmation for the one-shot invoice-verification decision. The bullets spell out every line the
 * confirm button touches — the decision is final for the whole PO, so it must be readable up front.
 */
const OcrBulkDecisionModal: React.FC<{
  variant: OcrBulkDecisionVariant | null;
  changes: string[];
  isSubmitting?: boolean;
  onConfirm: (decision: OcrBulkDecision) => void;
  onGoBack: () => void;
}> = ({ variant, changes, isSubmitting, onConfirm, onGoBack }) => {
  const copy = variant ? VARIANT_COPY[variant] : null;

  return (
    <Transition.Root show={Boolean(variant)} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onGoBack}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex justify-center items-end sm:items-center min-h-full p-4 sm:p-0 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative bg-white shadow-xl sm:my-8 p-5 rounded-lg sm:w-full sm:max-w-lg text-left transition-all transform overflow-hidden">
                <div className="sm:flex sm:items-start">
                  <div
                    className={`flex flex-shrink-0 justify-center items-center mx-auto sm:mx-0 rounded-full w-10 h-10 ${copy?.iconClassName ?? ""}`}
                  >
                    {variant === "accept" ? (
                      <CheckIcon className="w-5 h-5" aria-hidden="true" />
                    ) : (
                      <ExclamationTriangleIcon className="w-5 h-5" aria-hidden="true" />
                    )}
                  </div>
                  <div className="mt-3 sm:mt-0 sm:ml-4 w-full text-center sm:text-left">
                    <Dialog.Title as="h3" className="font-semibold text-base text-gray-900">
                      {copy?.title}
                    </Dialog.Title>
                    <p className="mt-1 text-gray-500 text-xs">{copy?.subtitle}</p>
                    <p className="mt-3 font-medium text-gray-900 text-xs">Changes</p>
                    <ul className="mt-1 space-y-1 pl-4 text-gray-600 text-xs list-disc">
                      {changes.length > 0 ? (
                        changes.map((change) => <li key={change}>{change}</li>)
                      ) : (
                        <li>No pending invoice differences on this purchase order.</li>
                      )}
                    </ul>
                  </div>
                </div>
                <div className="flex sm:flex-row flex-col-reverse sm:justify-end items-stretch sm:items-center gap-3 mt-6 pt-4 border-gray-100 border-t">
                  <button type="button" disabled={isSubmitting} className={GO_BACK_BUTTON_CLASSNAME} onClick={onGoBack}>
                    Go Back
                  </button>
                  {variant !== "accept" && (
                    <button
                      type="button"
                      disabled={isSubmitting}
                      className={REJECT_BUTTON_CLASSNAME}
                      onClick={() => onConfirm("reject")}
                    >
                      Reject all changes
                    </button>
                  )}
                  {variant !== "reject" && (
                    <button
                      type="button"
                      disabled={isSubmitting}
                      className={ACCEPT_BUTTON_CLASSNAME}
                      onClick={() => onConfirm("accept")}
                    >
                      Accept all changes
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default OcrBulkDecisionModal;
